import { useMemo, useState } from "react";
import { ArrowLeft, Check, Search, UserPlus, X } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AvailabilityPicker } from "../components/AvailabilityPicker";
import { SERVICE_OPTIONS } from "../data/services";
import { dateKeyFromIso, formatTime, toDateKey } from "../lib/date";
import { normalizeText } from "../lib/search";
import type { AppData, AppointmentFormValues, Id, Owner, Pet, ServiceType } from "../types";

interface AppointmentFormPageProps {
  data: AppData;
  createAppointment(values: AppointmentFormValues): unknown;
  updateAppointment(id: Id, values: AppointmentFormValues): void;
}

const blankValues = (date: string): AppointmentFormValues => ({
  petName: "",
  petSex: "unknown",
  breed: "",
  petNotes: "",
  ownerName: "",
  phone: "",
  email: "",
  date,
  startTime: "",
  durationMinutes: 60,
  serviceType: "",
  customServiceName: "",
  servicePricePln: "",
  appointmentNotes: "",
});

function valuesFromExisting(pet: Pet, owner: Owner, appointment: AppData["appointments"][number]): AppointmentFormValues {
  const durationMinutes = Math.max(30, Math.round((new Date(appointment.endsAt).getTime() - new Date(appointment.startsAt).getTime()) / 60_000));
  return {
    existingPetId: pet.id,
    petName: pet.name,
    petSex: pet.sex,
    breed: pet.breed || "",
    petNotes: pet.careNotes || "",
    ownerName: owner.fullName,
    phone: owner.phone,
    email: owner.email || "",
    date: dateKeyFromIso(appointment.startsAt),
    startTime: formatTime(appointment.startsAt),
    durationMinutes,
    serviceType: appointment.serviceType,
    customServiceName: appointment.customServiceName || "",
    servicePricePln: appointment.servicePricePln?.toString() || "",
    appointmentNotes: appointment.notes || "",
  };
}

interface PetAutocompleteProps {
  data: AppData;
  values: AppointmentFormValues;
  onChoose(pet: Pet, owner: Owner): void;
  onNew(): void;
}

function PetAutocomplete({ data, values, onChoose, onNew }: PetAutocompleteProps) {
  const [query, setQuery] = useState("");
  const matches = useMemo(() => {
    if (!query.trim()) return [];
    const normalized = normalizeText(query);
    return data.pets
      .map((pet) => ({ pet, owner: data.owners.find((owner) => owner.id === pet.ownerId) }))
      .filter((item): item is { pet: Pet; owner: Owner } => Boolean(item.owner))
      .filter(({ pet, owner }) => [pet.name, owner.fullName, owner.phone].some((value) => normalizeText(value).includes(normalized)))
      .slice(0, 5);
  }, [data, query]);

  return (
    <div className="autocomplete">
      <label className="field-label" htmlFor="existing-pet">Znajdź istniejącego psa</label>
      <div className="search-field search-field--compact">
        <Search size={18} aria-hidden="true" />
        <input id="existing-pet" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Pies, właściciel lub telefon" />
        {values.existingPetId && <button type="button" className="clear-search" aria-label="Wyczyść wybranego psa" onClick={onNew}><X size={16} /></button>}
      </div>
      {matches.length > 0 && (
        <div className="autocomplete-results">
          {matches.map(({ pet, owner }) => (
            <button key={pet.id} type="button" onClick={() => { onChoose(pet, owner); setQuery(""); }}>
              <span>{pet.avatar || "🐾"}</span><strong>{pet.name}</strong><small>{owner.fullName} · {owner.phone}</small>
            </button>
          ))}
        </div>
      )}
      <button type="button" className="new-client-button" onClick={() => { onNew(); setQuery(""); }}>
        <UserPlus size={16} /> {values.existingPetId ? "Wprowadź nowego klienta" : "Nowy klient"}
      </button>
    </div>
  );
}

export function AppointmentFormPage({ data, createAppointment, updateAppointment }: AppointmentFormPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { appointmentId } = useParams();
  const appointment = appointmentId ? data.appointments.find((item) => item.id === appointmentId) : undefined;
  const pet = appointment ? data.pets.find((item) => item.id === appointment.petId) : undefined;
  const owner = pet ? data.owners.find((item) => item.id === pet.ownerId) : undefined;
  const urlDate = new URLSearchParams(location.search).get("date") || toDateKey(new Date());
  const [values, setValues] = useState<AppointmentFormValues>(() => appointment && pet && owner ? valuesFromExisting(pet, owner, appointment) : blankValues(urlDate));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const editing = Boolean(appointment && pet && owner);

  function setValue<Key extends keyof AppointmentFormValues>(key: Key, value: AppointmentFormValues[Key]) {
    setValues((current) => ({ ...current, [key]: value }));
  }

  function fillExisting(selectedPet: Pet, selectedOwner: Owner) {
    setValues((current) => ({
      ...current,
      existingPetId: selectedPet.id,
      petName: selectedPet.name,
      petSex: selectedPet.sex,
      breed: selectedPet.breed || "",
      petNotes: selectedPet.careNotes || "",
      ownerName: selectedOwner.fullName,
      phone: selectedOwner.phone,
      email: selectedOwner.email || "",
    }));
  }

  function clearExisting() {
    setValues((current) => ({ ...current, existingPetId: undefined, petName: "", petSex: "unknown", breed: "", petNotes: "", ownerName: "", phone: "", email: "" }));
  }

  function validate() {
    const nextErrors: Record<string, string> = {};
    if (!values.date) nextErrors.date = "Wybierz datę";
    if (!values.startTime) nextErrors.startTime = "Wybierz godzinę";
    if (!values.petName.trim()) nextErrors.petName = "Podaj imię psa";
    if (!values.ownerName.trim()) nextErrors.ownerName = "Podaj właściciela";
    if (values.phone.replace(/\D/g, "").length < 9) nextErrors.phone = "Podaj poprawny telefon";
    if (!values.serviceType) nextErrors.serviceType = "Wybierz usługę";
    if (values.serviceType === "other" && !values.customServiceName.trim()) nextErrors.customServiceName = "Podaj nazwę usługi";
    if (values.servicePricePln !== "" && Number(values.servicePricePln) < 0) nextErrors.servicePricePln = "Cena nie może być ujemna";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!validate()) return;
    if (editing && appointment) updateAppointment(appointment.id, values);
    else createAppointment(values);
    navigate(`/?date=${values.date}`);
  }

  return (
    <main className="form-page app-shell">
      <header className="form-page__header">
        <button type="button" className="icon-button" aria-label="Wróć do agendy" onClick={() => navigate(`/?date=${values.date}`)}><ArrowLeft size={22} /></button>
        <h1>{editing ? "Edytuj wizytę" : "Dodaj wizytę"}</h1>
      </header>

      {appointmentId && !editing ? (
        <div className="empty-state"><span>🐾</span><h2>Nie ma tej wizyty</h2><button type="button" className="primary-button" onClick={() => navigate("/")}>Wróć do agendy</button></div>
      ) : (
        <form className="appointment-form" onSubmit={handleSubmit}>
          <AvailabilityPicker
            appointments={data.appointments}
            pets={data.pets}
            date={values.date}
            startTime={values.startTime}
            durationMinutes={values.durationMinutes}
            ignoredAppointmentId={appointment?.id}
            dateError={errors.date}
            timeError={errors.startTime}
            onDateChange={(date) => setValue("date", date)}
            onTimeChange={(time) => setValue("startTime", time)}
            onDurationChange={(duration) => setValue("durationMinutes", duration)}
          />

          <PetAutocomplete data={data} values={values} onChoose={fillExisting} onNew={clearExisting} />

          <section className="form-section"><h2>Podopieczny</h2>
            <div className="form-grid">
              <label><span>Imię psa *</span><input value={values.petName} onChange={(event) => setValue("petName", event.target.value)} placeholder="np. Larry" />{errors.petName && <small className="field-error">{errors.petName}</small>}</label>
              <label><span>Płeć</span><select value={values.petSex} onChange={(event) => setValue("petSex", event.target.value as AppointmentFormValues["petSex"])}><option value="unknown">Nie podano</option><option value="male">Pies</option><option value="female">Suczka</option></select></label>
              <label><span>Rasa</span><input value={values.breed} onChange={(event) => setValue("breed", event.target.value)} placeholder="opcjonalnie" /></label>
            </div>
            <label><span>Uwagi o psie</span><textarea value={values.petNotes} onChange={(event) => setValue("petNotes", event.target.value)} placeholder="np. wrażliwy na hałas" rows={3} /></label>
          </section>

          <section className="form-section"><h2>Właściciel</h2>
            <div className="form-grid">
              <label><span>Imię i nazwisko *</span><input value={values.ownerName} onChange={(event) => setValue("ownerName", event.target.value)} />{errors.ownerName && <small className="field-error">{errors.ownerName}</small>}</label>
              <label><span>Telefon *</span><input type="tel" value={values.phone} onChange={(event) => setValue("phone", event.target.value)} placeholder="501 234 567" />{errors.phone && <small className="field-error">{errors.phone}</small>}</label>
              <label><span>E-mail</span><input type="email" value={values.email} onChange={(event) => setValue("email", event.target.value)} placeholder="opcjonalnie" /></label>
            </div>
          </section>

          <section className="form-section"><h2>Usługa groomerska</h2>
            <div className="form-grid">
              <label><span>Typ usługi *</span><select value={values.serviceType} onChange={(event) => setValue("serviceType", event.target.value as ServiceType | "")}><option value="">Wybierz usługę</option>{SERVICE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>{errors.serviceType && <small className="field-error">{errors.serviceType}</small>}</label>
              <label><span>Cena usługi</span><input type="number" min="0" step="0.01" inputMode="decimal" value={values.servicePricePln} onChange={(event) => setValue("servicePricePln", event.target.value)} placeholder="np. 160" />{errors.servicePricePln && <small className="field-error">{errors.servicePricePln}</small>}</label>
            </div>
            {values.serviceType === "other" && <label><span>Nazwa usługi *</span><input value={values.customServiceName} onChange={(event) => setValue("customServiceName", event.target.value)} placeholder="np. pielęgnacja łap" />{errors.customServiceName && <small className="field-error">{errors.customServiceName}</small>}</label>}
            <label><span>Notatka do wizyty</span><textarea value={values.appointmentNotes} onChange={(event) => setValue("appointmentNotes", event.target.value)} rows={3} placeholder="opcjonalnie" /></label>
          </section>

          <button className="primary-button primary-button--wide" type="submit"><Check size={20} /> {editing ? "Zapisz zmiany" : "Dodaj wizytę"}</button>
        </form>
      )}
    </main>
  );
}
